import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, Input, SectionCard } from '@/components/ui';
import type { EndpointPathKey } from '@/api/console';
import { ENDPOINT_PATH_DEFAULTS, type EndpointPathDraft } from './endpointPaths';

const MACHINE_KEYS: ReadonlySet<EndpointPathKey> = new Set([
    'machine_tenants',
    'machine_memberships',
]);

interface AppEndpointPathsCardProps {
    title: string;
    keys: EndpointPathKey[];
    values: EndpointPathDraft;
    errors: Record<EndpointPathKey, string | null>;
    serverError: string | null;
    isChanged: (key: EndpointPathKey) => boolean;
    label: (base: ReactNode, changed: boolean) => ReactNode;
    onChange: (key: EndpointPathKey, value: string) => void;
}

/**
 * EAA-1709: the per-app paths the connection check (and, for `roles`, the catalog
 * sync) calls. Blank = the server default, which each placeholder shows. The two
 * machine paths only move the probe, so their hint says so.
 */
export function AppEndpointPathsCard({
    title,
    keys,
    values,
    errors,
    serverError,
    isChanged,
    label,
    onChange,
}: AppEndpointPathsCardProps) {
    const { t } = useTranslation();
    return (
        <SectionCard title={title}>
            <div className="portal-admin-form">
                {keys.map((key) => (
                    <FormField
                        key={key}
                        label={label(t(`console.appDetail.edit.endpoints.${key}`), isChanged(key))}
                        hint={
                            MACHINE_KEYS.has(key)
                                ? t('console.appDetail.edit.endpoints.machineHint')
                                : t('console.appDetail.edit.endpoints.hint')
                        }
                        error={errors[key] ?? (key === 'roles' ? serverError : null)}
                    >
                        <Input
                            value={values[key]}
                            placeholder={ENDPOINT_PATH_DEFAULTS[key]}
                            onChange={(e) => onChange(key, e.target.value)}
                            className="apps-create-mono-input"
                        />
                    </FormField>
                ))}
            </div>
        </SectionCard>
    );
}
